/**
 * «ملفي الصحي» — the patient builds their health profile step by step
 * (المرحلة 1.2, P01 and M01).
 *
 * Seven steps, in the plan's order: basics, measurements, chronic conditions
 * and medicines, allergies, family and surgical history, reports and imaging,
 * the insurance card. Any step can be left for later («تخطي وكمّل بعدين») and
 * picked up from the strip at the top. The percentage and which steps a
 * booking waits for are the server's (GET /api/health-profile, under the
 * `healthOnboarding` policy); the screen only shows them.
 *
 * The reports step is the existing «سجلات خارجية» upload, unchanged. The step
 * shown is in the address (?step=allergies), so the home card and the
 * pre-visit gate can open the one that is missing.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, ErrorState, Field, Icon, Input, PageHeader, Panel, Select, Spinner, Textarea, DateInput } from '../../components/common';
import { healthProfileApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { STEPS } from '../../domain/healthProfile';
import { ExternalRecords } from './ExternalRecords';
import { InsuranceCardCapture } from './InsuranceCardCapture';

export const STEP_ICONS = Object.freeze({
  basics: 'user-round',
  vitals: 'scale',
  conditions: 'pill',
  allergies: 'shield-alert',
  family: 'users',
  reports: 'file-text',
  insurance: 'credit-card',
});

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const SEVERITIES = ['mild', 'moderate', 'severe'];
const RELATIONS = ['father', 'mother', 'brother', 'sister', 'grandfather', 'grandmother', 'child'];
const COMMON_CONDITIONS = ['diabetes', 'hypertension', 'asthma', 'heart', 'thyroid', 'kidney'];

/* ── the strip: percentage and the seven steps ──────────────────────────── */

export function HealthProgress({ completeness = 0, compact = false }) {
  const { t } = useI18n();
  return (
    <div className={`hp-progress ${compact ? 'hp-progress--compact' : ''}`}>
      <div className="hp-progress__head">
        <span>{t('healthProfile.completeness')}</span>
        <strong data-testid="hp-percent">{completeness}%</strong>
      </div>
      <div className="hp-progress__track" role="progressbar" aria-valuenow={completeness} aria-valuemin={0} aria-valuemax={100} aria-label={t('healthProfile.completeness')}>
        <div className="hp-progress__bar" style={{ inlineSize: `${completeness}%` }} />
      </div>
    </div>
  );
}

function StepStrip({ steps, current, onPick }) {
  const { t } = useI18n();
  return (
    <ol className="hp-steps" aria-label={t('healthProfile.stepsLabel')}>
      {steps.map((step, index) => (
        <li key={step.id}>
          <button
            type="button"
            className={`hp-step ${step.done ? 'is-done' : ''} ${step.skipped ? 'is-skipped' : ''} ${current === step.id ? 'is-current' : ''}`}
            aria-current={current === step.id ? 'step' : undefined}
            onClick={() => onPick(step.id)}
            data-testid={`hp-step-${step.id}`}
          >
            <span className="hp-step__dot">{step.done ? <Icon name="check" size={14} /> : index + 1}</span>
            <span className="hp-step__label">{t(`healthProfile.steps.${step.id}`)}</span>
            {step.required && !step.done && <span className="hp-step__tag">{t('healthProfile.requiredTag')}</span>}
            {step.skipped && <span className="hp-step__tag hp-step__tag--skipped">{t('healthProfile.skippedTag')}</span>}
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Back, skip, save: the same three at the foot of every step. */
function StepActions({ onSave, saving, onSkip, onBack, saveLabel, saveVariant = 'primary', disabled = false }) {
  const { t } = useI18n();
  return (
    <div className="hp-actions">
      {onSave && (
        <Button variant={saveVariant} icon="check" onClick={onSave} loading={saving} disabled={disabled} data-testid="hp-save">
          {saveLabel || t('healthProfile.saveNext')}
        </Button>
      )}
      {onSkip && (
        <Button variant="ghost" icon="skip-forward" onClick={onSkip} disabled={saving} data-testid="hp-skip">
          {t('healthProfile.skip')}
        </Button>
      )}
      {onBack && (
        <Button variant="subtle" onClick={onBack} disabled={saving}>
          {t('common.back')}
        </Button>
      )}
    </div>
  );
}

/* ── the steps ──────────────────────────────────────────────────────────── */

function BasicsStep({ data, actions }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ dateOfBirth: data.basics.dateOfBirth || '', gender: data.basics.gender || '' });
  return (
    <>
      <div className="hp-grid">
        <Field label={t('healthProfile.basics.name')} hint={t('healthProfile.basics.nameHint')}>
          <Input value={data.basics.name || ''} readOnly />
        </Field>
        <Field label={t('healthProfile.basics.dateOfBirth')}>
          <DateInput value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} data-testid="hp-dob" />
        </Field>
        <Field label={t('healthProfile.basics.gender')}>
          <div className="hp-choice" role="radiogroup">
            {['female', 'male'].map((g) => (
              <button key={g} type="button" role="radio" aria-checked={form.gender === g} className={`hp-chip ${form.gender === g ? 'is-on' : ''}`} onClick={() => setForm({ ...form, gender: g })} data-testid={`hp-gender-${g}`}>
                {t(`profile.${g}`)}
              </button>
            ))}
          </div>
        </Field>
      </div>
      {actions(() => ({ dateOfBirth: form.dateOfBirth || null, gender: form.gender || null }))}
    </>
  );
}

function VitalsStep({ data, actions }) {
  const { t } = useI18n();
  const p = data.profile;
  const [form, setForm] = useState({ bloodType: p.bloodType || '', heightCm: p.heightCm ?? '', weightKg: p.weightKg ?? '' });
  const bmi = Number(form.heightCm) > 0 && Number(form.weightKg) > 0 ? (Number(form.weightKg) / (Number(form.heightCm) / 100) ** 2).toFixed(1) : null;
  return (
    <>
      <div className="hp-grid">
        <Field label={t('healthProfile.vitals.bloodType')}>
          <Select value={form.bloodType} onChange={(e) => setForm({ ...form, bloodType: e.target.value })} data-testid="hp-blood">
            <option value="">{t('healthProfile.vitals.bloodUnknown')}</option>
            {BLOOD_TYPES.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('healthProfile.vitals.height')}>
          <Input type="number" inputMode="numeric" min={40} max={250} value={form.heightCm} onChange={(e) => setForm({ ...form, heightCm: e.target.value })} data-testid="hp-height" />
        </Field>
        <Field label={t('healthProfile.vitals.weight')}>
          <Input type="number" inputMode="decimal" min={2} max={400} step="0.1" value={form.weightKg} onChange={(e) => setForm({ ...form, weightKg: e.target.value })} data-testid="hp-weight" />
        </Field>
      </div>
      {bmi && <p className="hp-note">{t('healthProfile.vitals.bmi', { bmi })}</p>}
      {actions(() => ({ bloodType: form.bloodType || null, heightCm: form.heightCm === '' ? null : form.heightCm, weightKg: form.weightKg === '' ? null : form.weightKg }))}
    </>
  );
}

/** A list of short texts: type and add, or tap a common one. */
function ChipList({ values, onChange, suggestions = [], placeholder, testId }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const add = (value) => {
    const text = String(value || '').trim();
    if (!text || values.includes(text)) return;
    onChange([...values, text]);
    setDraft('');
  };
  return (
    <div className="hp-chips">
      <div className="hp-chips__row">
        {values.map((v) => (
          <span key={v} className="hp-chip is-on">
            {v}
            <button type="button" aria-label={t('healthProfile.remove', { item: v })} onClick={() => onChange(values.filter((x) => x !== v))}>
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="hp-inline">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add(draft))} placeholder={placeholder} data-testid={testId} />
        <Button variant="ghost" icon="plus" onClick={() => add(draft)} disabled={!draft.trim()}>
          {t('healthProfile.add')}
        </Button>
      </div>
      {suggestions.length > 0 && (
        <div className="hp-chips__row">
          {suggestions
            .filter((s) => !values.includes(s))
            .map((s) => (
              <button key={s} type="button" className="hp-chip" onClick={() => add(s)}>
                <Icon name="plus" size={12} /> {s}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function ConditionsStep({ data, actions }) {
  const { t } = useI18n();
  const p = data.profile;
  const [conditions, setConditions] = useState(p.chronicConditions || []);
  const [medicines, setMedicines] = useState(p.currentMedications && p.currentMedications.length ? p.currentMedications : []);
  const [complaint, setComplaint] = useState(p.currentComplaint || '');
  const setMedicine = (index, key, value) => setMedicines(medicines.map((m, i) => (i === index ? { ...m, [key]: value } : m)));
  return (
    <>
      <Field label={t('healthProfile.conditions.chronic')} hint={t('healthProfile.conditions.chronicHint')}>
        <ChipList values={conditions} onChange={setConditions} suggestions={COMMON_CONDITIONS.map((c) => t(`healthProfile.conditions.common.${c}`))} placeholder={t('healthProfile.conditions.chronicPlaceholder')} testId="hp-condition" />
      </Field>
      <Field label={t('healthProfile.conditions.medicines')}>
        <div className="hp-rows">
          {medicines.map((m, index) => (
            <div key={index} className="hp-row">
              <Input value={m.name || ''} onChange={(e) => setMedicine(index, 'name', e.target.value)} placeholder={t('healthProfile.conditions.medicineName')} data-testid={`hp-med-${index}`} />
              <Input value={m.dose || ''} onChange={(e) => setMedicine(index, 'dose', e.target.value)} placeholder={t('healthProfile.conditions.dose')} />
              <Input value={m.frequency || ''} onChange={(e) => setMedicine(index, 'frequency', e.target.value)} placeholder={t('healthProfile.conditions.frequency')} />
              <button type="button" className="hp-row__remove" aria-label={t('healthProfile.remove', { item: m.name || '' })} onClick={() => setMedicines(medicines.filter((_, i) => i !== index))}>
                <Icon name="trash-2" size={15} />
              </button>
            </div>
          ))}
          <Button variant="ghost" icon="plus" onClick={() => setMedicines([...medicines, { name: '', dose: '', frequency: '' }])} data-testid="hp-med-add">
            {t('healthProfile.conditions.addMedicine')}
          </Button>
        </div>
      </Field>
      <Field label={t('healthProfile.conditions.complaint')} hint={t('healthProfile.conditions.complaintHint')}>
        <Textarea value={complaint} onChange={(e) => setComplaint(e.target.value)} rows={3} data-testid="hp-complaint" />
      </Field>
      {actions(() => ({ chronicConditions: conditions, currentMedications: medicines.filter((m) => m.name && m.name.trim()), currentComplaint: complaint }), {
        saveLabel: !conditions.length && !medicines.some((m) => m.name) ? t('healthProfile.conditions.none') : null,
      })}
    </>
  );
}

function AllergiesStep({ data, setData, actions }) {
  const { t } = useI18n();
  const { error: toastError } = useNotifications();
  const [form, setForm] = useState({ substance: '', reaction: '', severity: 'moderate' });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.substance.trim()) return;
    setBusy(true);
    try {
      const answer = await healthProfileApi.addAllergy(form);
      setData(answer);
      setForm({ substance: '', reaction: '', severity: 'moderate' });
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      setData(await healthProfileApi.removeAllergy(id));
    } catch (err) {
      toastError(err.message);
    }
  };

  return (
    <>
      {data.allergies.length > 0 && (
        <ul className="hp-allergies" data-testid="hp-allergy-list">
          {data.allergies.map((a) => (
            <li key={a.id} className={`hp-allergy hp-allergy--${a.severity}`}>
              <Icon name="shield-alert" size={16} />
              <span className="hp-allergy__name">{a.substance}</span>
              {a.reaction && <span className="hp-allergy__reaction">{a.reaction}</span>}
              <span className="hp-allergy__severity">{t(`healthProfile.allergies.severity.${a.severity}`)}</span>
              <button type="button" aria-label={t('healthProfile.remove', { item: a.substance })} onClick={() => remove(a.id)}>
                <Icon name="trash-2" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="hp-grid">
        <Field label={t('healthProfile.allergies.substance')}>
          <Input value={form.substance} onChange={(e) => setForm({ ...form, substance: e.target.value })} placeholder={t('healthProfile.allergies.substanceHint')} data-testid="hp-allergy-substance" />
        </Field>
        <Field label={t('healthProfile.allergies.reaction')}>
          <Input value={form.reaction} onChange={(e) => setForm({ ...form, reaction: e.target.value })} placeholder={t('healthProfile.allergies.reactionHint')} data-testid="hp-allergy-reaction" />
        </Field>
        <Field label={t('healthProfile.allergies.severityLabel')}>
          <Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="hp-allergy-severity">
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {t(`healthProfile.allergies.severity.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Button variant="ghost" icon="plus" onClick={add} loading={busy} disabled={!form.substance.trim()} data-testid="hp-allergy-add">
        {t('healthProfile.allergies.add')}
      </Button>
      {actions(() => ({}), { saveLabel: data.allergies.length ? t('healthProfile.saveNext') : t('healthProfile.allergies.none') })}
    </>
  );
}

function FamilyStep({ data, actions }) {
  const { t } = useI18n();
  const p = data.profile;
  const [family, setFamily] = useState(p.familyHistory || []);
  const [surgeries, setSurgeries] = useState(p.surgicalHistory || []);
  const setRow = (rows, set, index, key, value) => set(rows.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  return (
    <>
      <Field label={t('healthProfile.family.history')}>
        <div className="hp-rows">
          {family.map((f, index) => (
            <div key={index} className="hp-row">
              <Select value={f.relation || ''} onChange={(e) => setRow(family, setFamily, index, 'relation', e.target.value)}>
                <option value="">{t('healthProfile.family.relation')}</option>
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`healthProfile.family.relations.${r}`)}
                  </option>
                ))}
              </Select>
              <Input value={f.condition || ''} onChange={(e) => setRow(family, setFamily, index, 'condition', e.target.value)} placeholder={t('healthProfile.family.condition')} data-testid={`hp-family-${index}`} />
              <button type="button" className="hp-row__remove" aria-label={t('healthProfile.remove', { item: f.condition || '' })} onClick={() => setFamily(family.filter((_, i) => i !== index))}>
                <Icon name="trash-2" size={15} />
              </button>
            </div>
          ))}
          <Button variant="ghost" icon="plus" onClick={() => setFamily([...family, { relation: '', condition: '' }])} data-testid="hp-family-add">
            {t('healthProfile.family.add')}
          </Button>
        </div>
      </Field>
      <Field label={t('healthProfile.family.surgeries')}>
        <div className="hp-rows">
          {surgeries.map((s, index) => (
            <div key={index} className="hp-row">
              <Input value={s.procedure || ''} onChange={(e) => setRow(surgeries, setSurgeries, index, 'procedure', e.target.value)} placeholder={t('healthProfile.family.procedure')} />
              <Input type="number" inputMode="numeric" value={s.year || ''} onChange={(e) => setRow(surgeries, setSurgeries, index, 'year', e.target.value)} placeholder={t('healthProfile.family.year')} />
              <button type="button" className="hp-row__remove" aria-label={t('healthProfile.remove', { item: s.procedure || '' })} onClick={() => setSurgeries(surgeries.filter((_, i) => i !== index))}>
                <Icon name="trash-2" size={15} />
              </button>
            </div>
          ))}
          <Button variant="ghost" icon="plus" onClick={() => setSurgeries([...surgeries, { procedure: '', year: '' }])}>
            {t('healthProfile.family.addSurgery')}
          </Button>
        </div>
      </Field>
      {actions(() => ({ familyHistory: family.filter((f) => f.condition && f.condition.trim()), surgicalHistory: surgeries.filter((s) => s.procedure && s.procedure.trim()) }), {
        saveLabel: !family.some((f) => f.condition) && !surgeries.some((s) => s.procedure) ? t('healthProfile.family.none') : null,
      })}
    </>
  );
}

function ReportsStep({ data, reload, actions }) {
  const { t } = useI18n();
  return (
    <>
      <div className="hp-embed">
        <ExternalRecords onChange={reload} />
      </div>
      {actions(() => ({}), { saveLabel: data.reports ? t('healthProfile.saveNext') : t('healthProfile.reports.none') })}
    </>
  );
}

function InsuranceStep({ data, setData, actions, onDone }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [saving, setSaving] = useState(false);
  const save = async (payload) => {
    setSaving(true);
    try {
      const answer = await healthProfileApi.saveCard(payload);
      setData(answer);
      success(answer.insurance.status === 'accepted' ? t('healthProfile.card.accepted') : t('healthProfile.card.sent'));
      onDone(answer);
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <InsuranceCardCapture insurance={data.insurance} onSave={save} saving={saving} />
      {/* The card has its own save above; «no insurance» is the quieter way on. */}
      {actions(() => ({}), { saveLabel: t('healthProfile.card.noInsurance'), saveVariant: 'ghost' })}
    </>
  );
}

const STEP_VIEWS = { basics: BasicsStep, vitals: VitalsStep, conditions: ConditionsStep, allergies: AllergiesStep, family: FamilyStep, reports: ReportsStep, insurance: InsuranceStep };

/* ── the wizard ─────────────────────────────────────────────────────────── */

/** The step after `current` that is still open, else any open one, else null. */
function stepAfter(state, current) {
  const open = (id) => {
    const step = state.steps.find((s) => s.id === id);
    return step && !step.done;
  };
  const index = STEPS.indexOf(current);
  return STEPS.slice(index + 1).find(open) || STEPS.slice(0, index).find(open) || null;
}

export function HealthOnboardingWizard() {
  const { t, lang } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await healthProfileApi.get());
    } catch (err) {
      setError(err);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const asked = params.get('step');
  // Each step opens at the top, where its title and the strip are, not wherever the last one was left.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [asked]);
  const current = STEPS.includes(asked) ? asked : asked === 'done' ? 'done' : (data && data.next) || (data ? 'done' : 'basics');
  const go = useCallback((step) => setParams(step ? { step } : {}, { replace: false }), [setParams]);
  const moveOn = (state) => go(stepAfter(state, current) || 'done');

  const save = async (fields) => {
    setSaving(true);
    try {
      const state = await healthProfileApi.save(current, fields);
      setData(state);
      success(t('healthProfile.saved'));
      moveOn(state);
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const skip = async () => {
    setSaving(true);
    try {
      const state = await healthProfileApi.skip(current);
      setData(state);
      moveOn(state);
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return <Spinner variant="inline" label={t('healthProfile.loading')} />;

  const View = STEP_VIEWS[current];
  const index = STEPS.indexOf(current);
  const step = data.steps.find((s) => s.id === current);
  const actions = (collect, { saveLabel = null, saveVariant = 'primary' } = {}) => (
    <StepActions onSave={() => save(collect())} saving={saving} onSkip={step && !step.done ? skip : null} onBack={index > 0 ? () => go(STEPS[index - 1]) : null} saveLabel={saveLabel} saveVariant={saveVariant} />
  );

  return (
    <div className="hp-page" data-testid="health-onboarding">
      <PageHeader title={t('healthProfile.title')} subtitle={t('healthProfile.subtitle')} icon="heart-pulse" />
      <Panel className="hp-head">
        <HealthProgress completeness={data.completeness} />
        {data.missing.length > 0 && (
          <p className="hp-note hp-note--warn" data-testid="hp-required">
            <Icon name="alert-triangle" size={14} /> {t('healthProfile.requiredBefore', { steps: data.missing.map((s) => t(`healthProfile.steps.${s}`)).join(lang === 'en' ? ', ' : '، ') })}
          </p>
        )}
        <StepStrip steps={data.steps} current={current} onPick={go} />
      </Panel>

      {current === 'done' ? (
        <Panel className="hp-done" data-testid="hp-done">
          <Icon name="check-circle-2" size={40} />
          <h2>{data.completeness === 100 ? t('healthProfile.doneTitle') : t('healthProfile.doneSoFar')}</h2>
          <p>{data.completeness === 100 ? t('healthProfile.doneMessage') : t('healthProfile.doneSoFarMessage')}</p>
          <div className="hp-actions">
            <Link to="/patient" className="btn-primary">
              {t('healthProfile.toHome')}
            </Link>
            {data.next && (
              <Button variant="ghost" onClick={() => go(data.next)}>
                {t('healthProfile.continue', { step: t(`healthProfile.steps.${data.next}`) })}
              </Button>
            )}
          </div>
        </Panel>
      ) : (
        <Panel className="hp-step-panel" data-testid={`hp-panel-${current}`}>
          <header className="hp-step-panel__head">
            <span className="hp-step-panel__icon">
              <Icon name={STEP_ICONS[current]} size={22} />
            </span>
            <div>
              <p className="hp-step-panel__count">{t('healthProfile.stepOf', { n: index + 1, total: STEPS.length })}</p>
              <h2>{t(`healthProfile.steps.${current}`)}</h2>
              <p className="hp-step-panel__hint">{t(`healthProfile.hints.${current}`)}</p>
            </div>
          </header>
          {step && step.skipped && (
            <p className="hp-note hp-note--skipped">
              <Icon name="history" size={14} /> {t('healthProfile.skippedNote')}
            </p>
          )}
          <View key={current} data={data} setData={setData} reload={load} actions={actions} onDone={moveOn} />
        </Panel>
      )}
    </div>
  );
}

export default HealthOnboardingWizard;
